import { differenceInDays, startOfDay, subDays, subMonths } from 'date-fns';

/**
 * Format the xAxis date values to be "month day" names, such as "Sep 1" for September 1, 2019
 */
export function getMonthDayShortName(date: Date, locale = 'default'): string {
  if (!date) {
    return '';
  }
  // we are using a UTC timezone because Date objects have specific assumptions about timezones i.e UTC vs. local
  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Format the yAxis values and k-formats if necessary
 */
export function formatYAxisNumberScale(value: number): string {
  value = Math.trunc(value);
  return value > 999 ? Math.round(value / 100) / 10 + 'K' : value.toLocaleString();
}

// Returns the previous period date range given a date range and the time range option
export function previousTimePeriod(currentStart: Date, currentEnd: Date): [Date, Date] {
  const periodDurationDays = differenceInDays(currentEnd, currentStart) + 1;

  const previousStart = subDays(currentStart, periodDurationDays);
  const previousEnd = subDays(currentEnd, periodDurationDays);
  return [previousStart, previousEnd];
}

// Convert the number of seconds to be time string in format hh:mm:ss
// such as - converting 75 seconds to "00:01:15"
export function convertSecondsToTimeString(givenSeconds: number): string {
  const dateObj = new Date(givenSeconds * 1000);
  const hours = dateObj.getUTCHours();
  const minutes = dateObj.getUTCMinutes();
  const seconds = dateObj.getSeconds();

  return (
    hours.toString().padStart(2, '0') +
    ':' +
    minutes.toString().padStart(2, '0') +
    ':' +
    seconds.toString().padStart(2, '0')
  );
}

// Convert time string in format hh:mm:ss to be the number of seconds
// such as - converting "00:01:15" to 75 seconds
export function convertTimeStringToSeconds(givenTimeString: string): number {
  const a = givenTimeString.split(':');
  return +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
}

/*
Return the change direction from the sign of given number
 */
export function calcChangeDirection(change: number): string {
  const trends = ['negative-change', 'no-change', 'positive-change'];
  return trends[Math.sign(change) + 1];
}

/*
Return the trend from the sign of given number
 */
export function calcMetricTrend(change: number): string {
  const trends = ['trend-down', 'no-trend', 'trend-up'];
  return trends[Math.sign(change) + 1];
}

export function getDateBoundaries(timeRange: string): [Date, Date] {
  // Not include today
  const end = subDays(startOfDay(new Date()), 1);
  const dayOffsetMap = {
    '7days': {
      val: 7,
      type: 'days',
    },
    '30days': {
      val: 30,
      type: 'days',
    },
    '90days': {
      val: 90,
      type: 'days',
    },
    '6months': {
      val: 6,
      type: 'months',
    },
    '12months': {
      val: 12,
      type: 'months',
    },
    '16months': {
      val: 16,
      type: 'months',
    },
  };
  const offset = dayOffsetMap[timeRange];
  // A single day's stats would read the same start end
  const start = offset.type === 'months' ? subMonths(end, offset.val - 1) : subDays(end, offset.val - 1);
  return [start, end];
}

export function alignLocalDateToUTC(dString: string): Date {
  const d = new Date(dString);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
  );
}

export function alignUTC(dString: string): Date {
  const d = new Date(dString);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
}
